import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useVendorResponses, useEvaluateResponse, usePagination } from '../lib/hooks'
import { usePermissions } from '../contexts/IframeAuthProvider'
import { Button } from '../components/ui/Button'
import { Input, Select } from '../components/ui/Input'
import { StatusBadge } from '../components/ui/Badge'
import { LoadingSpinner, LoadingCard } from '../components/ui/LoadingSpinner'
import { 
  Search, 
  Filter, 
  Eye, 
  Download, 
  Play,
  Building,
  FileText,
  Calendar,
  Mail,
  Phone,
  MapPin
} from 'lucide-react'
import { formatDate, formatFileSize, formatRelativeTime } from '../lib/utils'
import { type VendorResponse, type VendorResponseCreateRequest } from '../lib/api'

export const VendorsPage: React.FC = () => {
  const permissions = usePermissions()
  const pagination = usePagination()
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [evaluationStatusFilter, setEvaluationStatusFilter] = useState('')

  // API hooks
  const { data: vendorsData, isLoading } = useVendorResponses(
    selectedProject || undefined,
    {
      skip: pagination.skip,
      limit: pagination.limit,
    }
  )

  const evaluateMutation = useEvaluateResponse()

  // Update pagination when data changes
  React.useEffect(() => {
    if (vendorsData) {
      pagination.setTotal(vendorsData.total_count)
    }
  }, [vendorsData, pagination.setTotal])


  const handleEvaluateResponse = async (responseId: string) => {
    try {
      await evaluateMutation.mutateAsync({ responseId })
    } catch (error) {
      // Error handled by mutation
    }
  }

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ]

  const evaluationStatusOptions = [
    { value: '', label: 'All Evaluation Statuses' },
    { value: 'not_evaluated', label: 'Not Evaluated' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Responses</h1>
          <p className="text-gray-600">Manage vendor responses and evaluations</p>
        </div>
        
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Select
              placeholder="Select project..."
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              options={[
                { value: '', label: 'Select a project' },
                // This would be populated from a projects API call
              ]}
            />
            
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
            
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
            />
            
            <Select
              placeholder="Filter by evaluation"
              value={evaluationStatusFilter}
              onChange={(e) => setEvaluationStatusFilter(e.target.value)}
              options={evaluationStatusOptions}
            />
            
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('')
                setEvaluationStatusFilter('')
              }}
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Vendor Responses List */}
      <div className="space-y-4">
        {!selectedProject ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a project</h3>
            <p className="text-gray-500">Choose a project to view vendor responses</p>
          </div>
        ) : isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <LoadingCard key={i} />
          ))
        ) : vendorsData?.data?.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No responses found</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter || evaluationStatusFilter
                ? 'No responses match your current filters.'
                : 'No vendor responses have been uploaded yet.'}
            </p>
          </div>
        ) : (
          vendorsData?.data?.map((response) => (
            <VendorResponseCard
              key={response.id}
              response={response}
              onEvaluate={() => handleEvaluateResponse(response.id)}
              canEvaluate={permissions.canEvaluateResponses}
              evaluating={evaluateMutation.isLoading}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {vendorsData && vendorsData.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, vendorsData.total_count)} of {vendorsData.total_count} responses
          </p>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.prevPage}
              disabled={!pagination.hasPrevPage}
            >
              Previous
            </Button>
            
            <span className="text-sm text-gray-700">
              Page {pagination.page + 1} of {vendorsData.total_pages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.nextPage}
              disabled={!pagination.hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}

    </div>
  )
}

// Vendor Response Card Component
interface VendorResponseCardProps {
  response: VendorResponse
  onEvaluate: () => void
  canEvaluate: boolean
  evaluating: boolean
}

const VendorResponseCard: React.FC<VendorResponseCardProps> = ({
  response,
  onEvaluate,
  canEvaluate,
  evaluating,
}) => {
  return (
    <div className="card">
      <div className="card-content">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{response.vendor_name}</h3>
              <StatusBadge status={response.status} />
              <StatusBadge status={response.evaluation_status} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  {response.vendor_email || 'No email provided'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="w-4 h-4 mr-2" />
                  {response.vendor_phone || 'No phone provided'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  {response.vendor_address || 'No address provided'}
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <FileText className="w-4 h-4 mr-2" />
                  {response.file_name}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  Submitted: {formatDate(response.submission_date)}
                </div>
                <div className="text-sm text-gray-600">
                  Size: {formatFileSize(response.file_size)}
                </div>
              </div>
            </div>
            
            {response.fit_score > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Fit Score</span>
                  <span className="text-sm font-medium text-green-600">{response.fit_score}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${response.fit_score}%` }}
                  />
                </div>
              </div>
            )}
            
            {response.evaluation_summary && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Evaluation Summary</h4>
                <p className="text-sm text-gray-600">{response.evaluation_summary}</p>
              </div>
            )}
            
            {response.notes && (
              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <h4 className="text-sm font-medium text-blue-700 mb-1">Notes</h4>
                <p className="text-sm text-blue-600">{response.notes}</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>
            
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
            
            {canEvaluate && response.evaluation_status !== 'completed' && (
              <Button 
                variant="primary" 
                size="sm"
                onClick={onEvaluate}
                loading={evaluating}
              >
                <Play className="w-4 h-4 mr-1" />
                Evaluate
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


export default VendorsPage